import { AiSlimBanner, Css, FormSectionLayout, HasIdAndName, useComputed } from "@homebound/beam";
import type { NamedFragment } from "src/fragments/commonFragments.generated";
import {
  MaterialCatalog_ItemFragment,
  MaterialCatalog_MaterialBrandFragment,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import { AdditionalDetailsSection } from "src/routes/libraries/material-catalog/material-listing-page/components/AdditionalDetailsSection";
import { MaterialDetailsSection } from "src/routes/libraries/material-catalog/material-listing-page/components/MaterialDetailsSection";
import { VariantDetailsSection } from "src/routes/libraries/material-catalog/material-listing-page/components/VariantDetailsSection";
import { useMaterialListingContext } from "src/routes/libraries/material-catalog/material-listing-page/components/material-listing-context/MaterialListingContext";

export type MaterialImportReviewStepProps = {
  items: MaterialCatalog_ItemFragment[];
  brands: MaterialCatalog_MaterialBrandFragment[];
  tags: NamedFragment[];
  markets: HasIdAndName[];
};

/**
 * The wizard's second step: the imported listing, in the same form the detail page edits.
 */
export function MaterialImportReviewStep(props: MaterialImportReviewStepProps) {
  const { items, brands, tags, markets } = props;
  const { variants, formState } = useMaterialListingContext();
  const initialized = useComputed(() => !!formState.id.value, [formState]);

  if (!initialized) {
    // TODO: There is a bug where at this point, the listing fields *should* be showing the values with a strike through on the 'prev' value
    // because we have not made it aware of optimistically created entities.
    // These fields were not showing the strike through because they were rendering before the form state was initialized.
    // This is a guard to prevent the wrong value snapshot value 'undefined' being used as the useAiProposal original value.
    // But I don't want to fix it until we have optimistic entity awareness in the ai fields.
    // return (
    //   <CenteredLayout size="sm">
    //     <AiLoadingPanel title="We're importing your material..." omitBg />
    //   </CenteredLayout>
    // );
  }

  return (
    <FormSectionLayout
      aiMode
      title="Review Imported Material Details"
      description="Please review the imported data highlighted below. You may edit or add to the form before saving."
      initialFields={
        variants.length > 1 ? (
          <div css={Css.mxPx(-24).$}>
            <AiSlimBanner
              title={`${variants.length} variants have been added and need review`}
              action={{ onClick: () => {}, label: "Next Variant", disabled: true, tooltip: "Not implemented yet" }}
            />
          </div>
        ) : undefined
      }
      sections={[
        { title: "Material Details", fields: <MaterialDetailsSection items={items} brands={brands} /> },
        { title: "Variant Details", fields: <VariantDetailsSection items={items} tags={tags} /> },
        { title: "Additional Details", fields: <AdditionalDetailsSection markets={markets} /> },
      ]}
    />
  );
}
